import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { useMutation, useAction } from "convex/react";
import { api } from "@convex/_generated/api";
import { trpc } from "@/lib/trpc";
import { toast } from "sonner";
import {
  Brain, Rocket, Key, CheckCircle2, ArrowRight, ArrowLeft, Loader2,
  Sparkles, Zap, Shield, BarChart3, Bot,
} from "lucide-react";

type AdAccount = {
  id: string;
  name: string;
  account_status: number;
  currency?: string;
  timezone_name?: string;
};

const STEPS = [
  { id: 0, title: "Bem-vindo", icon: Rocket },
  { id: 1, title: "Conectar Meta", icon: Key },
  { id: 2, title: "Selecionar Conta", icon: BarChart3 },
  { id: 3, title: "Pronto!", icon: CheckCircle2 },
];

export default function Onboarding() {
  const [step, setStep] = useState(0);
  const [accessToken, setAccessToken] = useState("");
  const [adAccounts, setAdAccounts] = useState<AdAccount[]>([]);
  const [selectedAccount, setSelectedAccount] = useState<AdAccount | null>(null);
  const [loading, setLoading] = useState(false);

  // F22 (2026-05-13): fetchAccounts agora existe em convex/meta.ts (action pública).
  const saveConfig = useMutation(api.meta.saveConfig);
  const fetchAccountsAction = useAction(api.meta.fetchAccounts);
  const completeOnboarding = useMutation(api.users.completeOnboarding);

  const [isFetchingAccounts, setIsFetchingAccounts] = useState(false);
  const fetchAccountsMutation = { isPending: isFetchingAccounts };

  const handleFetchAccounts = async () => {
    if (!accessToken.trim()) {
      toast.error("Cole seu token de acesso da Meta");
      return;
    }
    setIsFetchingAccounts(true);
    try {
      const accounts = (await fetchAccountsAction({
        accessToken: accessToken.trim(),
      })) as AdAccount[];
      if (!accounts || accounts.length === 0) {
        toast.error("Nenhuma conta de anúncio encontrada com esse token.");
        return;
      }
      setAdAccounts(accounts);
      setStep(2);
    } catch (err: any) {
      toast.error(`Erro: ${err?.message ?? "desconhecido"}`);
    } finally {
      setIsFetchingAccounts(false);
    }
  };

  const handleSelectAccount = async (account: AdAccount) => {
    setSelectedAccount(account);
    setLoading(true);
    try {
      await saveConfig({
        accessToken,
        adAccountId: account.id,
        adAccountName: account.name,
      });
      await completeOnboarding({});
      setStep(3);
    } catch (err: unknown) {
      const message =
        (err && typeof err === "object" && "message" in err
          ? String((err as { message?: unknown }).message ?? "")
          : "") || "erro desconhecido";
      toast.error(`Erro ao salvar: ${message}`);
    }
    setLoading(false);
  };

  const handleFinish = () => {
    window.location.href = "/";
  };

  return (
    <div className="min-h-screen bg-background relative overflow-hidden">
      {/* Background effects */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-emerald-500/5 rounded-full blur-[120px]" />
        <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-cyan-500/5 rounded-full blur-[120px]" />
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-primary/3 rounded-full blur-[150px]" />
      </div>

      <div className="relative z-10 flex flex-col items-center justify-center min-h-screen p-6">
        {/* Progress bar */}
        <div className="flex items-center gap-2 mb-12">
          {STEPS.map((s, i) => (
            <div key={s.id} className="flex items-center gap-2">
              <div
                className={`w-10 h-10 rounded-full flex items-center justify-center transition-all duration-500 ${
                  i <= step
                    ? "bg-primary text-primary-foreground shadow-lg shadow-primary/30"
                    : "bg-muted text-muted-foreground"
                }`}
              >
                {i < step ? (
                  <CheckCircle2 className="h-5 w-5" />
                ) : (
                  <s.icon className="h-5 w-5" />
                )}
              </div>
              {i < STEPS.length - 1 && (
                <div
                  className={`w-12 h-0.5 transition-all duration-500 ${
                    i < step ? "bg-primary" : "bg-muted"
                  }`}
                />
              )}
            </div>
          ))}
        </div>

        {/* Step content */}
        <div className="w-full max-w-lg">
          {/* Step 0: Welcome */}
          {step === 0 && (
            <div className="text-center space-y-8 animate-in fade-in duration-500">
              <div className="relative inline-block">
                <div className="h-24 w-24 rounded-2xl bg-gradient-to-br from-emerald-500/20 to-cyan-500/20 flex items-center justify-center mx-auto border border-emerald-500/20">
                  <Brain className="h-12 w-12 text-emerald-400" />
                </div>
                <div className="absolute -top-2 -right-2 h-8 w-8 rounded-full bg-primary flex items-center justify-center animate-bounce">
                  <Sparkles className="h-4 w-4 text-primary-foreground" />
                </div>
              </div>

              <div className="space-y-3">
                <h1 className="text-3xl font-bold tracking-tight">
                  Bem-vindo ao <span className="text-emerald-400">Manyflows ADS AI</span>
                </h1>
                <p className="text-muted-foreground text-lg max-w-md mx-auto">
                  Seu cérebro inteligente para análise e otimização de campanhas Meta Ads.
                </p>
              </div>

              <div className="grid grid-cols-3 gap-4 max-w-sm mx-auto">
                <div className="flex flex-col items-center gap-2 p-3 rounded-xl bg-card/50 border border-border/30">
                  <BarChart3 className="h-6 w-6 text-cyan-400" />
                  <span className="text-xs text-muted-foreground text-center">Dashboard Inteligente</span>
                </div>
                <div className="flex flex-col items-center gap-2 p-3 rounded-xl bg-card/50 border border-border/30">
                  <Bot className="h-6 w-6 text-emerald-400" />
                  <span className="text-xs text-muted-foreground text-center">IA Integrada</span>
                </div>
                <div className="flex flex-col items-center gap-2 p-3 rounded-xl bg-card/50 border border-border/30">
                  <Zap className="h-6 w-6 text-amber-400" />
                  <span className="text-xs text-muted-foreground text-center">Piloto Automático</span>
                </div>
              </div>

              <div className="flex items-center justify-center gap-2 text-sm text-muted-foreground">
                <Shield className="h-4 w-4" />
                <span>14 dias grátis para testar todas as funcionalidades</span>
              </div>

              <Button
                size="lg"
                onClick={() => setStep(1)}
                className="gap-2 shadow-lg shadow-primary/20 hover:shadow-xl hover:shadow-primary/30 transition-all"
              >
                Começar configuração
                <ArrowRight className="h-4 w-4" />
              </Button>
            </div>
          )}

          {/* Step 1: Connect Meta */}
          {step === 1 && (
            <div className="space-y-6 animate-in fade-in duration-500">
              <div className="text-center space-y-2">
                <h2 className="text-2xl font-bold">Conectar sua conta Meta Ads</h2>
                <p className="text-muted-foreground">
                  Cole seu token de acesso da Meta para conectar suas campanhas.
                </p>
              </div>

              <Card className="border-border/30 bg-card/50 backdrop-blur">
                <CardContent className="pt-6 space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="token" className="text-sm font-medium">
                      Token de Acesso
                    </Label>
                    <Input
                      id="token"
                      type="password"
                      placeholder="EAAXs3Gwau..."
                      value={accessToken}
                      onChange={(e) => setAccessToken(e.target.value)}
                      className="font-mono text-xs"
                    />
                  </div>

                  <div className="rounded-lg bg-muted/30 p-4 space-y-3">
                    <p className="text-xs font-semibold text-foreground flex items-center gap-1.5">
                      <Key className="h-3.5 w-3.5 text-primary" />
                      Como obter o token (passo a passo):
                    </p>
                    <ol className="text-xs text-muted-foreground space-y-2 list-decimal list-inside">
                      <li>
                        Acesse o{" "}
                        <a
                          href="https://developers.facebook.com/tools/explorer/"
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-primary hover:underline font-medium"
                        >
                          Graph API Explorer
                        </a>
                      </li>
                      <li>No campo <strong className="text-foreground">"Aplicativo do Meta"</strong>, selecione seu App (ou crie um em{" "}
                        <a href="https://developers.facebook.com/apps/" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">developers.facebook.com/apps</a>)
                      </li>
                      <li>Em <strong className="text-foreground">"Token do Usuário"</strong>, clique em <strong className="text-foreground">"Adicionar permissão"</strong></li>
                    </ol>

                    <div className="rounded-lg border border-amber-500/20 bg-amber-500/5 p-3 space-y-2">
                      <p className="text-xs font-semibold text-amber-400 flex items-center gap-1.5">
                        <Shield className="h-3.5 w-3.5" />
                        Permissões obrigatórias (marque TODAS):
                      </p>
                      <div className="grid grid-cols-1 gap-1.5">
                        <div className="flex items-center gap-2 text-xs">
                          <code className="bg-emerald-500/10 text-emerald-400 px-1.5 py-0.5 rounded font-mono text-[11px]">ads_read</code>
                          <span className="text-muted-foreground">Ler dados de campanhas e métricas</span>
                        </div>
                        <div className="flex items-center gap-2 text-xs">
                          <code className="bg-emerald-500/10 text-emerald-400 px-1.5 py-0.5 rounded font-mono text-[11px]">ads_management</code>
                          <span className="text-muted-foreground">Executar ações (pausar, escalar, alterar orçamento)</span>
                        </div>
                        <div className="flex items-center gap-2 text-xs">
                          <code className="bg-emerald-500/10 text-emerald-400 px-1.5 py-0.5 rounded font-mono text-[11px]">business_management</code>
                          <span className="text-muted-foreground">Acessar contas de anúncio vinculadas</span>
                        </div>
                      </div>
                    </div>

                    <ol className="text-xs text-muted-foreground space-y-2 list-decimal list-inside" start={4}>
                      <li>Clique em <strong className="text-foreground">"Gerar Token de Acesso"</strong> (botão azul)</li>
                      <li>Autorize no popup do Facebook (aceite todas as permissões)</li>
                      <li>Copie o token gerado e cole no campo acima</li>
                    </ol>

                    <div className="rounded-lg border border-blue-500/20 bg-blue-500/5 p-2.5">
                      <p className="text-[11px] text-blue-300">
                        <strong>Dica:</strong> O token gerado é de curta duração (1-2h). Após conectar, o sistema converte automaticamente para um token de longa duração (60 dias) se você configurar o App ID e App Secret nas Configurações.
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <div className="flex gap-3">
                <Button
                  variant="outline"
                  onClick={() => setStep(0)}
                  className="gap-2"
                >
                  <ArrowLeft className="h-4 w-4" />
                  Voltar
                </Button>
                <Button
                  onClick={handleFetchAccounts}
                  disabled={fetchAccountsMutation.isPending || !accessToken.trim()}
                  className="flex-1 gap-2"
                >
                  {fetchAccountsMutation.isPending ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <Key className="h-4 w-4" />
                  )}
                  Conectar e buscar contas
                </Button>
              </div>
            </div>
          )}

          {/* Step 2: Select Account */}
          {step === 2 && (
            <div className="space-y-6 animate-in fade-in duration-500">
              <div className="text-center space-y-2">
                <h2 className="text-2xl font-bold">Selecione sua conta de anúncios</h2>
                <p className="text-muted-foreground">
                  Encontramos {adAccounts.length} conta{adAccounts.length > 1 ? "s" : ""} vinculada{adAccounts.length > 1 ? "s" : ""} ao seu token.
                </p>
              </div>

              <div className="space-y-3 max-h-[400px] overflow-y-auto pr-1">
                {adAccounts.map((account) => (
                  <Card
                    key={account.id}
                    className={`border-border/30 bg-card/50 backdrop-blur cursor-pointer transition-all hover:border-primary/50 hover:bg-primary/5 ${
                      selectedAccount?.id === account.id ? "border-primary bg-primary/10" : ""
                    }`}
                    onClick={() => !loading && handleSelectAccount(account)}
                  >
                    <CardContent className="py-4 flex items-center justify-between">
                      <div className="space-y-1">
                        <p className="font-medium text-sm">{account.name}</p>
                        <p className="text-xs text-muted-foreground font-mono">{account.id}</p>
                        {account.currency && (
                          <div className="flex gap-2">
                            <Badge variant="outline" className="text-[10px]">{account.currency}</Badge>
                            {account.timezone_name && (
                              <Badge variant="outline" className="text-[10px]">{account.timezone_name}</Badge>
                            )}
                          </div>
                        )}
                      </div>
                      <div>
                        {account.account_status === 1 ? (
                          <Badge className="bg-emerald-500/10 text-emerald-400 border-emerald-500/20">Ativa</Badge>
                        ) : (
                          <Badge variant="outline" className="text-muted-foreground">Inativa</Badge>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>

              {loading && (
                <div className="flex items-center justify-center gap-2 text-sm text-muted-foreground">
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Salvando configuração...
                </div>
              )}

              <Button
                variant="outline"
                onClick={() => setStep(1)}
                disabled={loading}
                className="gap-2"
              >
                <ArrowLeft className="h-4 w-4" />
                Voltar
              </Button>
            </div>
          )}

          {/* Step 3: Done */}
          {step === 3 && (
            <div className="text-center space-y-8 animate-in fade-in duration-500">
              <div className="relative inline-block">
                <div className="h-24 w-24 rounded-full bg-emerald-500/20 flex items-center justify-center mx-auto">
                  <CheckCircle2 className="h-12 w-12 text-emerald-400" />
                </div>
                <div className="absolute inset-0 rounded-full border-2 border-emerald-500/30 animate-ping" />
              </div>

              <div className="space-y-3">
                <h2 className="text-3xl font-bold">Tudo pronto!</h2>
                <p className="text-muted-foreground text-lg max-w-md mx-auto">
                  Sua conta <span className="text-foreground font-medium">{selectedAccount?.name}</span> está conectada.
                  O cérebro já está analisando suas campanhas.
                </p>
              </div>

              <div className="rounded-xl bg-emerald-500/5 border border-emerald-500/20 p-4 max-w-sm mx-auto">
                <p className="text-sm text-emerald-400 font-medium">
                  🎉 Seu trial de 14 dias começou!
                </p>
                <p className="text-xs text-muted-foreground mt-1">
                  Explore todas as funcionalidades sem limitações.
                </p>
              </div>

              <Button
                size="lg"
                onClick={handleFinish}
                className="gap-2 shadow-lg shadow-primary/20"
              >
                <Rocket className="h-4 w-4" />
                Ir para o Dashboard
              </Button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
